import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useSportUrlParams, useSwitchValue } from '@mwl/core-lib';
import ArrowIcon from '@public/assets/common/icons/arrow.svg';
import { Button } from '@/components/Button/Button';
import { Tooltip } from '@/components/Tooltip/Tooltip';
import { CategoryFilterPopup } from '../CategoryFilterPopup/CategoryFilterPopup';
import { ChampionshipFilterPopup } from '../ChampionshipFilterPopup/ChampionshipFilterPopup';
import type { CountriesAndTournamentsFilterProps } from './CountriesAndTournamentsFilter.types';
import styles from './CountriesAndTournamentsFilter.module.scss';
export const CountriesAndTournamentsFilter: FC<CountriesAndTournamentsFilterProps> = ({
  className
}) => {
  const {
    t
  } = useTranslation('sport', {
    keyPrefix: 'filters'
  });
  const [isCountriesTooltipVisible, setCountriesTooltipVisible] = useState(false);
  const [isTournamentsTooltipVisible, setTournamentsTooltipVisible] = useState(false);
  const {
    categoryIds,
    championshipIds,
    sportId
  } = useSportUrlParams();
  const {
    value: isCategoriesModalOpen,
    on: openCategoriesModal,
    off: closeCategoriesModal
  } = useSwitchValue(false);
  const {
    value: isChampionshipsModalOpen,
    on: openChampionshipsModal,
    off: closeChampionshipsModal
  } = useSwitchValue(false);
  const categoriesDisabled = !sportId;
  const categoriesActive = !!categoryIds?.length;
  const championshipsDisabled = !categoriesActive;
  const championshipsActive = !!championshipIds?.length;
  const showTooltip = (type: 'countries' | 'tournaments') => {
    if (type === 'countries' && !isCountriesTooltipVisible) {
      setCountriesTooltipVisible(true);
      setTimeout(() => {
        setCountriesTooltipVisible(false);
      }, 1200);
    }
    if (type === 'tournaments' && !isTournamentsTooltipVisible) {
      setTournamentsTooltipVisible(true);
      setTimeout(() => {
        setTournamentsTooltipVisible(false);
      }, 1200);
    }
  };
  const handleOpenCategoriesModal = () => {
    if (categoriesDisabled) {
      showTooltip('countries');
    } else {
      openCategoriesModal();
    }
  };
  const handleOpenChampionshipsModal = () => {
    if (championshipsDisabled) {
      showTooltip('tournaments');
    } else {
      openChampionshipsModal();
    }
  };
  return <div className={cn(styles.root, className)} data-sentry-component="CountriesAndTournamentsFilter" data-sentry-source-file="CountriesAndTournamentsFilter.tsx">
      <Tooltip disabled={!!sportId} visible={isCountriesTooltipVisible} variant="arrow" placement="bottom" content={t('no_select_sport', 'Select a sport')} data-sentry-element="Tooltip" data-sentry-source-file="CountriesAndTournamentsFilter.tsx">
        <Button as="button" variant="outlined" color="grey" rounded size="sm" fullWidth uppercase={false} className={cn(styles.button, {
        [styles.disabled]: categoriesDisabled,
        [styles.active]: categoriesActive || !!sportId
      })} onClick={handleOpenCategoriesModal} data-sentry-element="Button" data-sentry-source-file="CountriesAndTournamentsFilter.tsx">
          <span className={styles.title}>
            {categoriesActive ? `${t('countries.countries_title', 'Countries')} (${categoryIds?.length})` : t('countries.all_countries', 'All countries')}
          </span>

          <span className={styles.arrowIcon}>
            <ArrowIcon data-sentry-element="ArrowIcon" data-sentry-source-file="CountriesAndTournamentsFilter.tsx" />
          </span>
        </Button>
      </Tooltip>

      <Tooltip disabled={championshipsActive} visible={isTournamentsTooltipVisible} variant="arrow" placement="bottom" content={t('no_select_country', 'Select a country')} data-sentry-element="Tooltip" data-sentry-source-file="CountriesAndTournamentsFilter.tsx">
        <Button as="button" variant="outlined" color="grey" rounded size="sm" fullWidth uppercase={false} className={cn(styles.button, {
        [styles.active]: championshipsActive || categoryIds?.length
      })} onClick={handleOpenChampionshipsModal} data-sentry-element="Button" data-sentry-source-file="CountriesAndTournamentsFilter.tsx">
          <span className={styles.title}>
            {championshipsActive ? `${t('tournaments.tournaments_title', 'Tournaments')} (${championshipIds?.length})` : t('tournaments.all_tournaments', 'All tournaments')}
          </span>

          <span className={styles.arrowIcon}>
            <ArrowIcon data-sentry-element="ArrowIcon" data-sentry-source-file="CountriesAndTournamentsFilter.tsx" />
          </span>
        </Button>
      </Tooltip>

      <CategoryFilterPopup show={isCategoriesModalOpen} onClose={closeCategoriesModal} data-sentry-element="CategoryFilterPopup" data-sentry-source-file="CountriesAndTournamentsFilter.tsx" />
      <ChampionshipFilterPopup show={isChampionshipsModalOpen} onClose={closeChampionshipsModal} data-sentry-element="ChampionshipFilterPopup" data-sentry-source-file="CountriesAndTournamentsFilter.tsx" />
    </div>;
};